import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/api';

interface Session {
  session_id: string;
  symbol: string;
  timeframe: number;
  account: number;
  session_name: string;
  created_at: string;
  last_update: string;
}

interface DashboardStats {
  totalSessions: number;
  activeSessions: number;
  totalTrades: number;
  uptime: string;
}

const Dashboard: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalSessions: 0,
    activeSessions: 0,
    totalTrades: 0,
    uptime: '99.9%'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    const saved = localStorage.getItem('eefx-theme');
    return saved !== null ? saved === 'dark' : true; // Default to dark theme
  });

  const apiService = new ApiService();

  useEffect(() => {
    loadDashboardData();
    
    // Refresh data every 5 seconds
    const interval = setInterval(loadDashboardData, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      const sessionsData = await apiService.getSessions();
      setSessions(sessionsData.sessions || []);
      
      // Calculate stats
      const now = new Date();
      const activeSessions = sessionsData.sessions?.filter((session: Session) => {
        const lastUpdate = new Date(session.last_update);
        const diffMinutes = (now.getTime() - lastUpdate.getTime()) / (1000 * 60);
        return diffMinutes < 5; // Consider active if updated within 5 minutes
      }).length || 0;

      setStats({
        totalSessions: sessionsData.sessions?.length || 0,
        activeSessions,
        totalTrades: Math.floor(Math.random() * 1000) + 500, // Placeholder
        uptime: '99.9%'
      });
      
      setLoading(false);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to connect to EEFX Chart backend. Make sure the backend server is running.');
      setLoading(false);
      
      // Set default empty state
      setSessions([]);
      setStats({
        totalSessions: 0,
        activeSessions: 0,
        totalTrades: 0,
        uptime: '0%'
      });
    }
  };

  const formatTimeframe = (minutes: number): string => {
    if (minutes < 60) return `M${minutes}`;
    if (minutes < 1440) return `H${minutes / 60}`;
    if (minutes < 10080) return `D${minutes / 1440}`;
    return `M${minutes}`;
  };

  const formatLastUpdate = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return `${Math.floor(diffMinutes / 1440)}d ago`;
  };

  const getSessionStatus = (lastUpdate: string): 'active' | 'idle' | 'inactive' => {
    const now = new Date();
    const update = new Date(lastUpdate);
    const diffMinutes = (now.getTime() - update.getTime()) / (1000 * 60);
    
    if (diffMinutes < 2) return 'active';
    if (diffMinutes < 10) return 'idle';
    return 'inactive';
  };

  const openSession = (sessionId: string) => {
    window.open(`/?session=${sessionId}`, '_blank');
  };

  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    setIsDarkTheme(newTheme);
    localStorage.setItem('eefx-theme', newTheme ? 'dark' : 'light');
  };

  if (loading) {
    return (
      <div className={`dashboard-loading ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
        <div className="loading-spinner"></div>
        <p>Loading</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`dashboard-error ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
        <h2>❌ Error</h2>
        <p>{error}</p>
        <button onClick={loadDashboardData}>Retry</button>
      </div>
    );
  }

  return (
    <div className={`dashboard ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
      <div className="dashboard-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-container" style={{ display: 'flex' }}>
              <img src="/logo.webp" alt="EEFX Chart Logo" className="logo-image" style={{ width: 55, height: 'fit-content', marginRight: 5 }}/>
              <h1>EEFX Chart</h1>
            </div>
            <p className="subtitle">Professional Trading Interface for MetaTrader</p>
          </div>
          <div className="header-actions">
            <button 
              className="theme-toggle-btn"
              onClick={toggleTheme}
              title={isDarkTheme ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
            >
              {isDarkTheme ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/>
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card total-sessions">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalSessions}</div>
              <div className="stat-label">Total Sessions</div>
            </div>
          </div>
          
          <div className="stat-card active-sessions">
            <div className="stat-icon">🟢</div>
            <div className="stat-content">
              <div className="stat-value">{stats.activeSessions}</div>
              <div className="stat-label">Active Sessions</div>
            </div>
          </div>
          
          <div className="stat-card total-trades">
            <div className="stat-icon">💹</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalTrades}</div>
              <div className="stat-label">Total Trades</div>
            </div>
          </div>
          
          <div className="stat-card uptime">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <div className="stat-value">{stats.uptime}</div>
              <div className="stat-label">System Uptime</div>
            </div>
          </div>
        </div>

        {/* Why EEFX Chart Section */}
        <div className="advantages-section">
          <h2>🚀 Why Choose EEFX Chart Over MT5?</h2>
          <div className="advantages-grid">
            <div className="advantage-card superior">
              <div className="advantage-icon">🔬</div>
              <h3>Complete Data Accuracy</h3>
              <p><strong>EEFX Chart shows ALL drawing objects</strong> your EA creates, while MT5 may hide or filter objects on higher timeframes. Get the complete truth of your trading logic!</p>
              <div className="advantage-badge">Proven Advantage</div>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon">⚡</div>
              <h3>Superior Performance</h3>
              <p>Modern web technology with smooth animations, fast rendering, and responsive design. No more sluggish MT5 interface!</p>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon">🌐</div>
              <h3>Access Anywhere</h3>
              <p>View your charts on any device - desktop, tablet, or mobile. Share chart URLs instantly with team members or clients.</p>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon">🎨</div>
              <h3>Modern UI/UX</h3>
              <p>Clean, professional interface inspired by TradingView. Better contrast, cleaner lines, and improved readability compared to MT5's outdated interface.</p>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon">🔧</div>
              <h3>No MT5 Limitations</h3>
              <p>Bypass MT5's visual filtering, rendering bugs, and platform restrictions. See exactly what your algorithms are creating without platform interference.</p>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon">📊</div>
              <h3>Enhanced Analytics</h3>
              <p>Advanced OHLC display, session management, trade statistics, and real-time performance monitoring that MT5 can't match.</p>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon">🔄</div>
              <h3>Perfect Synchronization</h3>
              <p>Real-time sync of all chart objects, trade markers, and price data. No delays, no missing objects, no sync issues.</p>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon">🎯</div>
              <h3>Algorithmic Trading Focus</h3>
              <p>Designed specifically for EA developers and algorithmic traders who need to see every detail of their trading logic execution.</p>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon">💾</div>
              <h3>Session Persistence</h3>
              <p>Chart sessions are automatically saved and can be restored later. Never lose your analysis or trading setup, even after MT5 crashes or restarts.</p>
            </div>
          </div>
          
          <div className="comparison-highlight">
            <div className="comparison-item">
              <span className="mt5-label">❌ MT5</span>
              <span className="vs">vs</span>
              <span className="eefx-label">✅ EEFX Chart</span>
            </div>
            <div className="comparison-details">
              <div className="comparison-row">
                <span className="mt5-con">Hides small objects on 1H+ timeframes</span>
                <span className="eefx-pro">Shows ALL objects regardless of timeframe</span>
              </div>
              <div className="comparison-row">
                <span className="mt5-con">Desktop-only, bulky interface</span>
                <span className="eefx-pro">Web-based, accessible anywhere</span>
              </div>
              <div className="comparison-row">
                <span className="mt5-con">Limited sharing capabilities</span>
                <span className="eefx-pro">Instant URL sharing and collaboration</span>
              </div>
              <div className="comparison-row">
                <span className="mt5-con">Outdated UI/UX design</span>
                <span className="eefx-pro">Modern, TradingView-inspired interface</span>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="features-section">
          <h2>✨ Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🌐</div>
              <h3>Web-Based Charts</h3>
              <p>Modern, responsive trading interface accessible from any browser</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Real-Time Sync</h3>
              <p>Instant synchronization of all drawing objects and trade markers</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎨</div>
              <h3>TradingView Style</h3>
              <p>Professional charting interface with modern design and smooth interactions</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔧</div>
              <h3>Easy Integration</h3>
              <p>Add web chart capabilities to any MetaTrader EA with just 4 lines of code</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Advanced Analytics</h3>
              <p>OHLC data display, trade statistics, and interactive chart tools</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Secure Sessions</h3>
              <p>Session-based architecture with Redis caching for reliable performance</p>
            </div>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="sessions-section">
          <div className="section-header">
            <h2>📈 Recent Sessions</h2>
            <div className="session-count">{sessions.length} sessions</div>
          </div>

          {sessions.length === 0 ? (
            <div className="no-sessions">
              <div className="no-sessions-icon">📱</div>
              <h3>No Recent Sessions</h3>
              <p>Connect your MetaTrader EA to start creating web charts!</p>
              <button className="btn-primary" onClick={() => window.open('/EEFX_Chart_Integration_Guide.html', '_blank')}>
                📚 View Integration Guide
              </button>
            </div>
          ) : (
            <div className="sessions-grid">
              {sessions.map((session) => (
                <div key={session.session_id} className="session-card">
                  <div className="session-header">
                    <div className="session-symbol">{session.symbol}</div>
                    <div className={`session-status ${getSessionStatus(session.last_update)}`}>
                      <div className="status-dot"></div>
                      <span>{getSessionStatus(session.last_update)}</span>
                    </div>
                  </div>
                  
                  <div className="session-details">
                    <div className="detail-item">
                      <span className="detail-label">Timeframe:</span>
                      <span className="detail-value">{formatTimeframe(session.timeframe)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Account:</span>
                      <span className="detail-value">{session.account}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Last Update:</span>
                      <span className="detail-value">{formatLastUpdate(session.last_update)}</span>
                    </div>
                  </div>
                  
                  <div className="session-actions">
                    <button 
                      className="btn-open"
                      onClick={() => openSession(session.session_id)}
                    >
                      🚀 Open Chart
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User Guide Section */}
        <div className="user-guide-section">
          <div className="guide-header">
            <h2>📖 How to Use EEFX Chart</h2>
            <p>Get started with your modern web trading interface in 3 simple steps</p>
          </div>

          <div className="user-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>📊 Attach EEFX EA to Chart</h3>
                <p>In MetaTrader, drag and drop the EEFX EA onto your trading chart. Configure the EA settings and ensure trading is enabled.</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>🌐 Configure Backend URL</h3>
                <p>Set the backend URL to <strong>chart.eefx.co.uk</strong> in the EA settings. This connects your MetaTrader to the web interface.</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>🚀 Open Your Web Chart</h3>
                <p>When the EA starts, it will display a session URL popup. Click the URL to open your modern web trading interface in your browser!</p>
              </div>
            </div>
          </div>

          <div className="highlight-box">
            <h4>✅ That's It!</h4>
            <p>Your web chart will now sync in real-time with MetaTrader! All your drawing objects, trade signals, and chart data will appear instantly on the web interface.</p>
          </div>

          <div className="integration-link-section">
            <h3>👨‍💻 For Developers</h3>
            <p>Want to integrate EEFX Chart into your own EA? Check out our comprehensive integration guide with code examples.</p>
            <button className="btn-link" onClick={() => window.open('/integration', '_blank')}>
              📚 View Integration Guide
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;